// RULES WITH GATES, IN CONSTRUCTION TRACKING - the same logic as the
// Timeline viewer's filter-gates page (owner, 2026-09-21: "follow the same
// logic of the filter gates page in timelineviewer", "use dropdown from the
// data to make it easy").
//
// As on that page:
//   - "Add Rule" opens a form where EACH RULE CHOOSES ITS OWN GATE;
//   - the rule is PICKED FROM THE DATA in a cascade - categories, then the
//     groups within them, then levels and statuses - never typed;
//   - an optional condition on a measure (the page's Parameter / Logic /
//     Value, which it built and switched off);
//   - the rules sit in a table with a Logic gate column, with Reset and
//     Run Filter.
//
// And the one place it deliberately differs: rules of the SAME gate combine
// by that gate (AND all, OR any, NOT none), and the gate groups are then
// all required. The Timeline page's logicGate overwrites its answer on each
// pass, so only its LAST gate group counts; here every group counts, and
// the panel says so.
//
// SAVE AND LOAD, FOR THE COMPANY (owner, 2026-09-21: "build saved and
// loaded rules for the company"), as that page saves its filters - but a
// set here belongs to the company of whoever saved it and is offered on
// every project the company works on. Loading a set RUNS it, and names
// anything it asks for that this model does not have, rather than
// quietly matching nothing.
//
// THE RULES ARE PART OF THE FILTER, NOT A SECOND ONE: Run Filter hands them
// up into the same object as the header filters, resolved once by the
// server for the tree, the select-all count, the preview and the write.
import React, { useEffect, useState } from "react";
import { Button, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, message } from "antd";
import { popupInStage, stageContainer } from "./stagePopup";
import { CloseOutlined, DeleteOutlined, DownloadOutlined, FilterOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import { GATES } from "components/Share/logicGate";
import { deleteRuleSet, getQuantityValues, getRuleSets, saveRuleSet } from "api/tracking/trackingElementsApi";
import { STATUS_LABEL, STATUS_ORDER } from "./trackingTheme";
import { DETAIL_DIMENSIONS, DETAIL_SUMS, unitOf } from "./quantityFormat";
import { matchOption } from "components/SearchSelect/searchSelect";

const T = { muted: "#6B7280", line: "#E5E7EB", warn: "#b45309" };

// The measure a condition can test - the page's "Parameter".
export const MEASURES = [...DETAIL_SUMS, ...DETAIL_DIMENSIONS].map((c) => ({ value: c.key, label: c.label, col: c }));
// The page's "Logic", each meaning ONE thing (that page maps Contains and
// Equals to the same operator).
export const LOGICS = [
  { value: "more", label: "More than" },
  { value: "less", label: "Less than" },
  { value: "equals", label: "Equals" },
  { value: "different", label: "Different" },
  { value: "hasValue", label: "Has value" },
  { value: "none", label: "Is empty" },
];
const needsValue = (op) => !["hasValue", "none"].includes(op);

export const EMPTY_RULE = { gate: "AND", categories: [], groups: [], levels: [], statuses: [], condition: null };
const normal = (r, gate) => ({ ...EMPTY_RULE, gate: (r && r.gate) || gate || "AND", ...r });

/** A rule is usable when something was picked, or a complete condition set. */
export function isUsable(r) {
  if (!r) return false;
  const picked = ["categories", "groups", "levels", "statuses"].some((k) => (r[k] || []).length);
  const c = r.condition;
  const cond = !!(c && c.field && c.op && (!needsValue(c.op)
    || (c.value !== "" && c.value !== null && c.value !== undefined && Number.isFinite(Number(c.value)))));
  return picked || cond;
}

const gateLabel = (g) => (GATES.find((x) => x.value === g) || {}).label || g;

/** The rules as the server takes them - what Run Filter sends and what Save stores. */
export const cleanRules = (rows) => rows.map((r) => ({
  gate: r.gate, categories: r.categories, groups: r.groups, levels: r.levels, statuses: r.statuses,
  ...(r.condition && r.condition.field ? { condition: r.condition } : {}),
}));

/** The names a set asks for that the lists read from THIS model do not
 *  hold. A list cut short proves nothing missing, so it is not judged. */
export function missingNames(rows, lists) {
  const out = [];
  const judge = (label, key, list, skip = []) => {
    if (!list || list.truncated) return;
    const have = new Set((list.values || []).map((v) => v.value));
    const want = [...new Set(rows.flatMap((r) => r[key] || []))].filter((x) => !skip.includes(x));
    want.filter((x) => !have.has(x)).forEach((x) => out.push(`${label} "${x}"`));
  };
  judge("category", "categories", lists.category);
  judge("group", "groups", lists.group);
  judge("level", "levels", lists.level, ["(no level)"]);
  return out;
}

// canSave (2026-10-14): saving rules for the company is a Construction
// tracking Add on the project team's side. Absent = as before.
export default function RuleBuilder({ rules = [], gate = "AND", units, onApply, projectId = null, documentIds = "", listId = "", running = false, canSave = true }) {
  // The TABLE is the draft; Run Filter applies it. A rule added but not run
  // is not a filter yet - and the panel says so rather than leaving the
  // table and the tree silently disagreeing.
  const rulesKey = JSON.stringify(rules);
  const [table, setTable] = useState(() => rules.map((r) => normal(r, gate)));
  useEffect(() => { setTable(rules.map((r) => normal(r, gate))); }, [rulesKey, gate]); // eslint-disable-line react-hooks/exhaustive-deps
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_RULE);

  // Choices read from the model, with counts. Groups are asked for WITHIN
  // the categories picked - the cascade - so the list never offers a group
  // the rule could not match.
  const [lists, setLists] = useState({ category: null, group: null, level: null });
  const load = (field, extra = {}) => {
    if (!projectId) return;
    setLists((p) => ({ ...p, [field]: { loading: true, values: [] } }));
    getQuantityValues(projectId, { field, scope: 1, ...(documentIds ? { documentIds } : {}), ...(listId ? { listId } : {}), ...extra })
      .then((r) => setLists((p) => ({ ...p, [field]: { loading: false, values: r.values || [], truncated: !!r.truncated, blank: !!r.blank } })))
      .catch((e) => setLists((p) => ({ ...p, [field]: { loading: false, values: [], error: e.message || "Could not read the values" } })));
  };
  useEffect(() => { if (adding) { load("category"); load("level"); } }, [adding]); // eslint-disable-line react-hooks/exhaustive-deps
  const catsKey = form.categories.join("|");
  useEffect(() => {
    if (!adding) return;
    if (!form.categories.length) { setLists((p) => ({ ...p, group: null })); return; }
    load("group", { categories: JSON.stringify(form.categories) });
  }, [adding, catsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const opts = (field, extraBlank) => {
    const l = lists[field];
    const o = ((l && l.values) || []).map((v) => ({ value: v.value, label: `${v.value} · ${Number(v.n).toLocaleString()}` }));
    if (extraBlank && l && l.blank) o.push({ value: "(no level)", label: "(no level)" });
    return o;
  };
  const listNote = (field) => {
    const l = lists[field];
    if (!l) return null;
    if (l.error) return <span style={{ color: T.warn }}>{l.error}</span>;
    // A list cut short says so rather than looking complete.
    if (l.truncated) return <span style={{ color: T.muted }}>first 500 shown</span>;
    return null;
  };

  const pending = JSON.stringify(table) !== JSON.stringify(rules.map((r) => normal(r, gate)));
  const gatesUsed = [...new Set(table.map((r) => r.gate))];

  const run = (rows) => onApply(cleanRules(rows), "AND");

  // ---- saved sets ----
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveBusy, setSaveBusy] = useState(false);
  const [setName, setSetName] = useState("");        // the set the table came from, if any
  const [loadOpen, setLoadOpen] = useState(false);
  const [sets, setSets] = useState({ loading: false, sets: [], reason: "", error: "" });
  const readSets = () => {
    if (!projectId) return;
    setSets((p) => ({ ...p, loading: true, error: "" }));
    getRuleSets(projectId)
      .then((r) => setSets({ loading: false, sets: r.sets || [], reason: r.reason || "", error: "" }))
      .catch((e) => setSets({ loading: false, sets: [], reason: "", error: e.message || "Could not read the saved rules" }));
  };
  useEffect(() => { if (loadOpen) readSets(); }, [loadOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (replace = false) => {
    const name = saveName.trim();
    if (!name) return;
    setSaveBusy(true);
    try {
      const r = await saveRuleSet(projectId, { name, rules: cleanRules(table), gate: "AND", ...(replace ? { replace: true } : {}) });
      message.success(`${r.replaced ? "Replaced" : "Saved"} "${r.set.name}" for your company`);
      setSetName(r.set.name);
      setSaveOpen(false);
    } catch (e) {
      if (e.status === 409 && e.data && e.data.existing) {
        const ex = e.data.existing;
        Modal.confirm({
          title: `Your company already has rules called "${ex.name}"`,
          content: `Saved by ${ex.createdByName || "someone in your company"}, ${ex.rules.length} rule${ex.rules.length === 1 ? "" : "s"}. Replace them with these ${table.length}?`,
          okText: "Replace", cancelText: "Keep theirs",
          getContainer: stageContainer,
          onOk: () => save(true),
        });
      } else {
        message.error(e.message || "Could not save the rules");
      }
    } finally {
      setSaveBusy(false);
    }
  };

  // Load = put the set in the table AND run it. Then say what it names that
  // this model does not have: a set saved on another project can ask for a
  // category this one never had, and would otherwise just match nothing.
  const loadSet = async (x) => {
    const rows = (x.rules || []).map((r) => normal(r, x.gate));
    setTable(rows);
    setSetName(x.name);
    setLoadOpen(false);
    run(rows);
    message.success(`Loaded and run: "${x.name}"`);
    if (!projectId) return;
    const cats = [...new Set(rows.flatMap((r) => r.categories || []))];
    const wantGroups = rows.some((r) => (r.groups || []).length);
    const ask = (field, extra = {}) => getQuantityValues(projectId, { field, scope: 1, ...(documentIds ? { documentIds } : {}), ...(listId ? { listId } : {}), ...extra })
      .then((r) => ({ values: r.values || [], truncated: !!r.truncated })).catch(() => null);
    const [category, level, group] = await Promise.all([
      ask("category"), ask("level"),
      wantGroups ? ask("group", cats.length ? { categories: JSON.stringify(cats) } : {}) : Promise.resolve(null),
    ]);
    const missing = missingNames(rows, { category, level, group });
    if (missing.length) {
      message.warning(`Not found in what this project tracks: ${missing.slice(0, 6).join(", ")}${missing.length > 6 ? ` and ${missing.length - 6} more` : ""}. The rules naming them match nothing here.`, 10);
    }
  };
  const removeSet = async (x) => {
    try {
      await deleteRuleSet(projectId, x._id);
      message.success(`Deleted "${x.name}"`);
      if (setName === x.name) setSetName("");
      readSets();
    } catch (e) { message.error(e.message || "Could not delete the rules"); }
  };

  const any = <span style={{ color: T.muted }}>any</span>;
  const columns = [
    { title: "Logic gate", key: "gate", width: 90, render: (_, r) => <Tag color={r.gate === "NOT" ? "red" : r.gate === "OR" ? "blue" : "default"}>{r.gate}</Tag> },
    { title: "Categories", key: "c", render: (_, r) => (r.categories.length ? r.categories.join(", ") : any) },
    { title: "Groups", key: "g", render: (_, r) => (r.groups.length ? r.groups.join(", ") : any) },
    { title: "Levels", key: "l", width: 120, render: (_, r) => (r.levels.length ? r.levels.join(", ") : any) },
    { title: "Status", key: "s", width: 130, render: (_, r) => (r.statuses.length ? r.statuses.map((s) => STATUS_LABEL[s] || s).join(", ") : any) },
    {
      title: "Condition", key: "cond", width: 170,
      render: (_, r) => {
        const c = r.condition;
        if (!c || !c.field) return <span style={{ color: T.muted }}>–</span>;
        const m = MEASURES.find((x) => x.value === c.field);
        const lg = LOGICS.find((x) => x.value === c.op);
        return `${m ? m.label : c.field} ${lg ? lg.label.toLowerCase() : c.op}${needsValue(c.op) ? ` ${c.value}` : ""}`;
      },
    },
    {
      title: "", key: "x", width: 36,
      render: (_, r, i) => (
        <Tooltip getPopupContainer={popupInStage} title="Remove this rule">
          <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => setTable((t) => t.filter((_, j) => j !== i))} />
        </Tooltip>
      ),
    },
  ];

  const cond = form.condition || { field: undefined, op: "more", value: "" };
  const condCol = (MEASURES.find((m) => m.value === cond.field) || {}).col;
  const formOk = isUsable(form);

  return (
    <div style={{ padding: "8px 10px", marginBottom: 8, border: `1px solid ${T.line}`, borderRadius: 6, background: "#FAFBFC" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 8, flexWrap: "wrap" }}>
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => { setForm(EMPTY_RULE); setAdding(true); }}>Add Rule</Button>
        <Space size={6}>
          <Button size="small" icon={<ReloadOutlined />} onClick={() => { setTable([]); setSetName(""); onApply([], "AND"); }}>Reset</Button>
          <Tooltip getPopupContainer={popupInStage} title={table.length ? "Save these rules for everyone in your company" : "Add a rule first"}>
            <Button size="small" icon={<SaveOutlined />} disabled={!table.length || !projectId} style={canSave ? undefined : { display: "none" }}
              onClick={() => { setSaveName(setName); setSaveOpen(true); }}>Save</Button>
          </Tooltip>
          <Button size="small" icon={<DownloadOutlined />} disabled={!projectId} onClick={() => setLoadOpen(true)}>Load</Button>
          <Button size="small" type="primary" icon={<FilterOutlined />} loading={running} disabled={!pending && !running} onClick={() => run(table)}>
            {running ? "Filtering…" : "Run Filter"}
          </Button>
        </Space>
      </div>
      {setName ? <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>Saved rules: <b style={{ color: "#111827" }}>{setName}</b></div> : null}
      <Table getPopupContainer={popupInStage}
        size="small" rowKey={(r) => JSON.stringify(r)} pagination={false} dataSource={table} columns={columns}
        locale={{ emptyText: "No rules yet - Add Rule, then Run Filter" }}
      />
      <div style={{ fontSize: 11.5, color: T.muted, marginTop: 6 }}>
        {/* How the gates combine, said where the rules are - the ambiguity is
            real, and the page this follows combines them differently. */}
        {gatesUsed.length > 1
          ? `Rules of the same gate combine by it (AND: all, OR: any, NOT: none). An element must satisfy every gate used here: ${gatesUsed.join(", ")}.`
          : (gatesUsed.length === 1 ? gateLabel(gatesUsed[0]) : "")}
        {pending && table.length ? <span style={{ color: T.warn, marginLeft: 8 }}>not run yet - press Run Filter</span> : null}
      </div>

      <Modal getContainer={stageContainer}
        open={saveOpen} title="Save rules for your company" okText="Save" onCancel={() => setSaveOpen(false)}
        okButtonProps={{ disabled: !saveName.trim(), loading: saveBusy }} onOk={() => save(false)} destroyOnClose
      >
        <div style={{ fontSize: 12, marginBottom: 2 }}>Name</div>
        <Input autoFocus maxLength={120} value={saveName} placeholder="e.g. EMT on L1 not installed"
          onChange={(e) => setSaveName(e.target.value)} onPressEnter={() => { if (saveName.trim()) save(false); }} />
        <div style={{ fontSize: 11.5, color: T.muted, marginTop: 8 }}>
          {table.length} rule{table.length === 1 ? "" : "s"}. Everyone in your company can load them, on every project.
          {pending ? " Saved as they are in the table - they have not been run yet." : ""}
        </div>
      </Modal>

      {/* The footer carries the way OUT to the full list. This modal is where
          somebody is already looking for a saved filter, so it is where they
          find out that a page listing all of them exists at all - a page only
          reachable by knowing it exists is not a choice anyone made.
          A new tab, not a navigation: the rule table behind this modal holds
          work that has not been saved yet. */}
      <Modal getContainer={stageContainer} open={loadOpen} title="Load saved rules" onCancel={() => setLoadOpen(false)} width={640} destroyOnClose
        footer={(
          <div style={{ textAlign: "left" }}>
            <a href="/filter-view" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
              See all saved filters
            </a>
            <span style={{ fontSize: 12, color: T.muted, marginLeft: 6 }}>
              - your company's tracking rules and timeline filters, in one list
            </span>
          </div>
        )}>
        {sets.error ? <div style={{ color: T.warn, marginBottom: 6 }}>{sets.error}</div> : null}
        <Table getPopupContainer={popupInStage}
          size="small" rowKey="_id" loading={sets.loading} dataSource={sets.sets} pagination={{ pageSize: 8, hideOnSinglePage: true }}
          locale={{ emptyText: sets.reason || "Your company has no saved rules yet - build some, then Save" }}
          columns={[
            { title: "Name", key: "n", render: (_, x) => <b>{x.name}</b> },
            {
              title: "Rules", key: "r", width: 130,
              render: (_, x) => `${x.rules.length} (${[...new Set(x.rules.map((r) => r.gate || x.gate || "AND"))].join(" + ")})`,
            },
            {
              title: "Saved by", key: "b", width: 170,
              render: (_, x) => (
                <span style={{ fontSize: 12 }}>
                  {x.updatedByName || x.createdByName || "–"}
                  {x.updatedAt ? <span style={{ color: T.muted }}> · {new Date(x.updatedAt).toLocaleDateString()}</span> : null}
                </span>
              ),
            },
            {
              title: "", key: "a", width: 110,
              render: (_, x) => (
                <Space size={4}>
                  <Button size="small" type="primary" onClick={() => loadSet(x)}>Load</Button>
                  {x.canChange ? (
                    <Popconfirm getPopupContainer={popupInStage} title={`Delete "${x.name}" for everyone in your company?`} okText="Delete" onConfirm={() => removeSet(x)}>
                      <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label={`Delete ${x.name}`} />
                    </Popconfirm>
                  ) : null}
                </Space>
              ),
            },
          ]}
        />
      </Modal>

      <Modal getContainer={stageContainer}
        open={adding} title="Add Rule" okText="Apply" onCancel={() => setAdding(false)}
        okButtonProps={{ disabled: !formOk }}
        onOk={() => { setTable((t) => [...t, form]); setAdding(false); }}
        destroyOnClose
      >
        <Space direction="vertical" size={10} style={{ width: "100%" }}>
          <div>
            <div style={{ fontSize: 12, marginBottom: 2 }}>Logic</div>
            <Select getPopupContainer={popupInStage} style={{ width: "100%" }} value={form.gate} onChange={(v) => setForm((f) => ({ ...f, gate: v }))}
              options={GATES.map((g) => ({ value: g.value, label: g.label }))} />
          </div>
          <div>
            <div style={{ fontSize: 12, marginBottom: 2 }}>Categories {listNote("category")}</div>
            <Select filterOption={matchOption} getPopupContainer={popupInStage} mode="multiple" allowClear showSearch optionFilterProp="label" style={{ width: "100%" }}
              placeholder={lists.category && lists.category.loading ? "reading the model…" : "Any category"}
              loading={!!(lists.category && lists.category.loading)}
              value={form.categories}
              // Picking different categories clears groups that belonged to
              // the old ones - a group outside the categories matches nothing.
              onChange={(v) => setForm((f) => ({ ...f, categories: v, groups: [] }))}
              options={opts("category")} />
          </div>
          {form.categories.length ? (
            <div>
              <div style={{ fontSize: 12, marginBottom: 2 }}>
                Groups <span style={{ color: T.muted }}>within those categories</span> {listNote("group")}
              </div>
              <Select filterOption={matchOption} getPopupContainer={popupInStage} mode="multiple" allowClear showSearch optionFilterProp="label" style={{ width: "100%" }}
                placeholder={lists.group && lists.group.loading ? "reading the model…" : "Any group"}
                loading={!!(lists.group && lists.group.loading)}
                value={form.groups} onChange={(v) => setForm((f) => ({ ...f, groups: v }))}
                options={opts("group")} />
            </div>
          ) : null}
          <div>
            <div style={{ fontSize: 12, marginBottom: 2 }}>Levels {listNote("level")}</div>
            <Select filterOption={matchOption} getPopupContainer={popupInStage} mode="multiple" allowClear showSearch optionFilterProp="label" style={{ width: "100%" }}
              placeholder="Any level" value={form.levels} onChange={(v) => setForm((f) => ({ ...f, levels: v }))}
              options={opts("level", true)} />
          </div>
          <div>
            <div style={{ fontSize: 12, marginBottom: 2 }}>Status</div>
            <Select getPopupContainer={popupInStage} mode="multiple" allowClear style={{ width: "100%" }} placeholder="Any status"
              value={form.statuses} onChange={(v) => setForm((f) => ({ ...f, statuses: v }))}
              options={STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s] }))} />
          </div>
          <div>
            <div style={{ fontSize: 12, marginBottom: 2 }}>Condition <span style={{ color: T.muted }}>optional</span></div>
            <Space size={6} wrap>
              <Select getPopupContainer={popupInStage} allowClear style={{ width: 150 }} placeholder="Parameter" value={cond.field}
                onChange={(v) => setForm((f) => ({ ...f, condition: v ? { ...cond, field: v } : null }))}
                options={MEASURES.map((m) => ({ value: m.value, label: m.label }))} />
              <Select getPopupContainer={popupInStage} style={{ width: 120 }} value={cond.op} disabled={!cond.field}
                onChange={(v) => setForm((f) => ({ ...f, condition: { ...cond, op: v } }))} options={LOGICS} />
              {needsValue(cond.op) ? (
                <InputNumber style={{ width: 130 }} disabled={!cond.field} value={cond.value === "" ? null : cond.value}
                  placeholder={condCol && condCol.unit ? unitOf(condCol, units) : "value"}
                  onChange={(v) => setForm((f) => ({ ...f, condition: { ...cond, value: v === null ? "" : v } }))} />
              ) : null}
            </Space>
          </div>
          {!formOk ? (
            <div style={{ fontSize: 11.5, color: T.muted }}>Pick at least one category, group, level or status, or set a condition.</div>
          ) : null}
        </Space>
      </Modal>
    </div>
  );
}
